import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, runTransaction } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { SoundControl } from '../components/SoundControl';
import { COUNTRY_DEFAULTS, MAX_PLAYERS } from '../constants/gameConstants';
import { useToast, ToastContainer } from '../components/ToastNotification';

const LobbyScreen = ({ roomCode, onGameStart, onLeaveLobby, currentUser, isMuted, toggleMute, playSound }) => {
  const [players, setPlayers] = useState([]);
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toasts, showToast, removeToast } = useToast();
  const lobbyMusicRef = useRef(null);

  // Mobil kontroller kaldırıldı.

  const myPlayer = players.find(p => p.id === currentUser?.uid);
  const isHost = roomData?.hostId === currentUser?.uid;

  useEffect(() => {
    const musicUrl = '/sounds/loading.mp3'; // Lobi müziği için bir ses dosyası

    if (!lobbyMusicRef.current) {
      lobbyMusicRef.current = new Audio(musicUrl);
      lobbyMusicRef.current.loop = true;
      lobbyMusicRef.current.volume = 0.5;
    }

    if (!isMuted) {
      const playPromise = lobbyMusicRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn("Lobi müziği otomatik çalınamadı:", error);
        });
      }
    } else {
      lobbyMusicRef.current.pause();
    }

    return () => {
      if (lobbyMusicRef.current) {
        lobbyMusicRef.current.pause();
        lobbyMusicRef.current.currentTime = 0;
      }
    };
  }, [isMuted]);

  useEffect(() => {
    if (lobbyMusicRef.current) {
      if (isMuted) {
        lobbyMusicRef.current.pause();
      } else {
        const playPromise = lobbyMusicRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn("Lobi müziği sessizden çıkarılınca çalınamadı:", error);
          });
        }
      }
    }
  }, [isMuted]);

  useEffect(() => {
    if (!roomCode) return;

    const roomRef = doc(db, 'rooms', roomCode);
    const unsubscribe = onSnapshot(roomRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setRoomData(data);
        setPlayers(data.players || []);
        setLoading(false);

        if (data.status === 'setup' || data.status === 'playing') {
          onGameStart(roomCode);
        }
      } else {
        onLeaveLobby();
      }
    });

    return () => unsubscribe();
  }, [roomCode, onGameStart, onLeaveLobby]);

  const handleLogout = () => {
    signOut(auth);
    onLeaveLobby();
  };

  const leaveLobby = async () => {
    if (!currentUser || !roomCode) return;
    try {
      const roomRef = doc(db, 'rooms', roomCode);

      await runTransaction(db, async (transaction) => {
        const roomDoc = await transaction.get(roomRef);
        if (!roomDoc.exists()) return;

        const currentData = roomDoc.data();
        const playerToRemove = currentData.players.find(p => p.id === currentUser.uid);

        if (playerToRemove) {
          const updatedPlayers = currentData.players.filter(p => p.id !== currentUser.uid);
          transaction.update(roomRef, { players: updatedPlayers });
        }
      });
    } catch (error) {
      console.error('Lobiden ayrılma hatası:', error);
    }
    onLeaveLobby();
  };

  const handleSlotClick = async (country, playerInSlot) => {
    if (!myPlayer) return;

    const roomRef = doc(db, 'rooms', roomCode);

    if (playerInSlot) {
      if (playerInSlot.id === currentUser.uid) {
        playSound('click', 0.3);
        await runTransaction(db, async (transaction) => {
          const roomDoc = await transaction.get(roomRef);
          if (!roomDoc.exists()) throw 'Oda bulunamadı';

          const currentPlayers = roomDoc.data().players;
          const updatedPlayers = currentPlayers.map(p => {
            if (p.id === currentUser.uid) {
              return { ...p, country: null, code: null, color: '#808080' };
            }
            return p;
          });
          transaction.update(roomRef, { players: updatedPlayers });
        });
        return;
      }

      if (isHost) {
        playSound('failure', 0.5);
        await updateDoc(roomRef, { players: arrayRemove(playerInSlot) });
        showToast(`${playerInSlot.name} odadan çıkarıldı`, 'warning', 2500);
        return;
      }

      return;
    }

    if (isHost) {
      if (!myPlayer.country) {
        playSound('click', 0.5);
        await runTransaction(db, async (transaction) => {
          const roomDoc = await transaction.get(roomRef);
          if (!roomDoc.exists()) throw 'Oda bulunamadı';

          const currentPlayers = roomDoc.data().players;
          const updatedPlayers = currentPlayers.map(p => {
            if (p.id === currentUser.uid) {
              return { ...p, country: country.code, code: country.code, color: country.color };
            }
            return p;
          });
          transaction.update(roomRef, { players: updatedPlayers });
        });
        return;
      } else {
        if (players.length >= MAX_PLAYERS) {
          showToast(`Oda dolu! Maksimum ${MAX_PLAYERS} oyuncu olabilir.`, 'error');
          return;
        }
        playSound('click', 0.5);
        const botName = `${country.name} Bot`;
        const botId = `bot-${Date.now()}`;
        const newBot = {
          id: botId,
          name: botName,
          isHost: false,
          isBot: true,
          code: country.code,
          country: country.code,
          color: country.color,
          attackPower: 0,
          defensePower: 0,
        };
        await updateDoc(roomRef, { players: arrayUnion(newBot) });
        return;
      }
    } else {
      playSound('click', 0.5);
      await runTransaction(db, async (transaction) => {
        const roomDoc = await transaction.get(roomRef);
        if (!roomDoc.exists()) throw 'Oda bulunamadı';

        const currentPlayers = roomDoc.data().players;
        const updatedPlayers = currentPlayers.map(p => {
          if (p.id === currentUser.uid) {
            return { ...p, country: country.code, code: country.code, color: country.color };
          }
          return p;
        });
        transaction.update(roomRef, { players: updatedPlayers });
      });
      return;
    }
  };

  const handleStartGame = async () => {
    if (!isHost) return;

    const allPlayersReady = players.every(p => !!p.country);

    if (players.length < 2) {
      showToast('Oyunu başlatmak için en az 2 oyuncu gerekli!', 'warning');
      return;
    }

    if (!allPlayersReady) {
      showToast('Tüm oyuncuların bir ülke seçmesi gerekiyor!', 'warning');
      return;
    }

    try {
      const roomRef = doc(db, 'rooms', roomCode);
      await updateDoc(roomRef, { status: 'setup' });
      playSound('game-start', 0.5);
      showToast('Oyun başlıyor! 🚀', 'success', 2000);
    } catch (error) {
      console.error('Oyunu başlatma hatası:', error);
      showToast('Oyun başlatılırken bir hata oluştu!', 'error');
    }
  };

  if (loading || !roomData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⚙️</div>
          <p className="text-slate-300">Lobi yükleniyor...</p>
        </div>
      </div>
    );
  }

  const canStartGame = isHost && players.length >= 2 && players.every(p => !!p.country);
  const waitingPlayers = players.filter(p => !p.country);
  const readyPlayers = players.filter(p => !!p.country);

  const styles = `
    @keyframes fadeInScale {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    .fade-in-scale {
      animation: fadeInScale 0.5s ease-out;
    }

    .glass-panel {
      background: rgba(15, 23, 42, 0.4);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(148, 163, 184, 0.1);
    }

    .btn-primary {
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
      transition: all 0.3s ease;
    }

    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.3);
    }

    .btn-danger {
      background: transparent;
      border: 1px solid rgba(239, 68, 68, 0.3);
      color: #f87171;
      transition: all 0.3s ease;
    }

    .btn-danger:hover:not(:disabled) {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.5);
      transform: translateY(-2px);
    }

    .player-card {
      background: rgba(30, 41, 59, 0.5);
      border: 1px solid rgba(148, 163, 184, 0.1);
      transition: all 0.3s ease;
    }

    .player-card:hover {
      background: rgba(30, 41, 59, 0.7);
      border-color: rgba(148, 163, 184, 0.2);
    }

    .country-slot {
      background: rgba(30, 41, 59, 0.6);
      border: 2px solid rgba(148, 163, 184, 0.2);
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .country-slot:hover {
      border-color: rgba(99, 102, 241, 0.5);
      background: rgba(30, 41, 59, 0.8);
      transform: translateY(-2px);
    }

    .country-slot.selected {
      border-color: rgba(34, 197, 94, 0.5);
      background: rgba(34, 197, 94, 0.1);
    }

    .country-slot.taken-enemy {
      border-color: rgba(239, 68, 68, 0.5);
      background: rgba(239, 68, 68, 0.1);
      cursor: not-allowed;
    }

    .country-slot.taken-bot {
      border-color: rgba(99, 102, 241, 0.5);
      background: rgba(99, 102, 241, 0.1);
    }
  `;

  // Sadece Masaüstü Düzeni (Önceki Mobile Layout kaldırıldı)
  return (
    <div className="h-screen w-screen flex flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-hidden">
      <style>{styles}</style>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Header */}
      <header className="flex-shrink-0 glass-panel border-b border-slate-700 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">AXIO WAR</h1>
            <p className="text-sm text-slate-400 mt-1">Lobi • Oda Kodu: <span className="font-mono font-bold text-blue-400">{roomCode}</span></p>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-400">Oyuncular</div>
            <div className="text-2xl font-bold text-white">{players.length}/{MAX_PLAYERS}</div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Side - Players */}
        <aside className="w-80 glass-panel border-r border-slate-700 flex flex-col p-6 overflow-hidden">
          <h2 className="text-lg font-bold text-white mb-4">Oyuncular</h2>

          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {readyPlayers.map(p => (
              <div key={p.id} className="player-card p-3 rounded-lg flex items-center gap-3">
                <img
                  src={`https://flagcdn.com/w40/${p.country?.toLowerCase()}.png`}
                  alt={p.country}
                  style={{ width: '32px', height: 'auto', borderRadius: '2px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: p.color }}>
                    {p.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {p.id === currentUser.uid && '(Sen) '}
                    {p.isHost && '👑 HOST '}
                    {p.isBot && '🤖 Bot'}
                  </div>
                </div>
              </div>
            ))}

            {waitingPlayers.length > 0 && (
              <div className="pt-3 border-t border-slate-700">
                <h3 className="text-xs font-bold text-slate-400 mb-3">Bekleyen Oyuncular</h3>
                {waitingPlayers.map(p => (
                  <div key={p.id} className="player-card p-3 rounded-lg flex items-center gap-3 mb-2 animate-pulse">
                    <span className="text-lg">❓</span>
                    <div className="flex-1">
                      <div className="text-sm font-bold">{p.name}</div>
                      <div className="text-xs text-slate-500">Seçim bekleniyor...</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="mt-6 pt-6 border-t border-slate-700 space-y-3">
            {isHost && (
              <button
                onClick={handleStartGame}
                disabled={!canStartGame}
                className="w-full py-3 px-4 text-white font-bold rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🚀 Oyunu Başlat
              </button>
            )}

            <div className="space-y-2">
              <button
                onClick={leaveLobby}
                className="w-full py-2 px-3 btn-danger rounded-lg font-semibold text-sm"
              >
                Lobiden Ayrıl
              </button>
              <button
                onClick={handleLogout}
                className="w-full py-2 px-3 btn-danger rounded-lg font-semibold text-sm"
              >
                Çıkış Yap
              </button>
            </div>

            <div className="flex justify-center pt-2">
              <SoundControl isMuted={isMuted} onToggleMute={toggleMute} />
            </div>
          </div>
        </aside>

        {/* Right Side - Country Selection */}
        <main className="flex-1 flex flex-col p-8 overflow-hidden">
          <h2 className="text-2xl font-bold text-white mb-6">
            {isHost ? '🎮 Ülkeleri Ata' : '🌍 Ülke Seç'}
          </h2>

          {/* 20 ülkeyi (4x5 veya 5x4) düzeninde göstermek için 5 sütun kullanılır */}
          <div className="flex-1 grid grid-cols-5 gap-5 overflow-y-auto pr-2">
            {COUNTRY_DEFAULTS.map((country) => {
              const playerInSlot = players.find(p => p.country === country.code);
              const isMe = playerInSlot?.id === currentUser.uid;

              let slotClass = 'country-slot';
              if (playerInSlot) {
                if (isMe) {
                  slotClass += ' selected';
                } else if (playerInSlot.isBot) {
                  slotClass += ' taken-bot';
                } else {
                  slotClass += ' taken-enemy';
                }
              }

              return (
                <div
                  key={country.code}
                  className={`${slotClass} p-6 rounded-xl flex flex-col items-center justify-center text-center`}
                  onClick={() => handleSlotClick(country, playerInSlot)}
                >
                  <img
                    src={`https://flagcdn.com/w160/${country.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w80/${country.code.toLowerCase()}.png 1x, https://flagcdn.com/w160/${country.code.toLowerCase()}.png 2x`}
                    alt={country.name}
                    style={{
                      width: '80px',
                      height: 'auto',
                      marginBottom: '12px',
                      borderRadius: '4px'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-lg font-bold text-white block">{country.name}</span>
                  <span className="text-xs text-slate-400 mt-2">
                    {playerInSlot ? (
                      <span style={{ color: playerInSlot.color }}>
                        {isMe ? '✓ SEN' : playerInSlot.name}
                        {playerInSlot.isBot ? ' 🤖' : ''}
                      </span>
                    ) : (
                      'Boş'
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LobbyScreen;